import { useCallback, useState } from 'react';
import urlReformatPhotos from '@alltrails/lightbox/utils/urlReformatPhotos';
import type { TrailId } from '@alltrails/types/TrailId';
import type TrailPhoto from '@alltrails/types/TrailPhoto';
import { getCarouselPhotos, getPhotos } from '../api/requests';

type LoadState = 'idle' | 'loaded';

export default function useTrailPhotos({ defaultPhoto, perPage, trailId }: { defaultPhoto?: TrailPhoto; perPage?: number; trailId: TrailId }) {
  const initialPhotos = defaultPhoto ? [defaultPhoto] : [];
  const [loadState, setLoadState] = useState<LoadState>('idle');
  const [photos, setPhotos] = useState<TrailPhoto[]>(initialPhotos);
  const [lightboxPhotos, setLightboxPhotos] = useState<{ id: number; imgUrl: string }[]>(urlReformatPhotos(initialPhotos));
  const [secondPhoto, setSecondPhoto] = useState<TrailPhoto | undefined>(undefined);

  const loadPhotos = useCallback(async () => {
    const [response, carouselResponse] = await Promise.all([
      getPhotos(trailId, perPage ? { per_page: perPage } : undefined),
      getCarouselPhotos(trailId)
    ]);
    setLoadState('loaded');

    if (!response?.photos?.length) {
      return;
    }

    const carouselPhoto = carouselResponse?.locations?.[1]?.photo;
    let tempPhotos: TrailPhoto[] = [...response.photos];

    if (defaultPhoto) {
      if (response.photos[0].id === defaultPhoto?.id) {
        const [_, ...rest] = tempPhotos;
        tempPhotos = [defaultPhoto, ...rest];
      } else {
        tempPhotos = [defaultPhoto, ...tempPhotos];
      }
    }

    if (carouselPhoto) {
      if (tempPhotos[1]?.id === carouselPhoto.id) {
        tempPhotos[1] = carouselPhoto;
      } else {
        tempPhotos.splice(1, 0, carouselPhoto);
      }
    }

    setPhotos(tempPhotos);
    setSecondPhoto(tempPhotos[1]);
    setLightboxPhotos(urlReformatPhotos(tempPhotos));
  }, [defaultPhoto, perPage, trailId]);

  return { photos, lightboxPhotos, loadPhotos, loadState, secondPhoto };
}
